import {
    startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth,
    subDays, subWeeks, subMonths, differenceInCalendarDays, format, isValid,
} from 'date-fns';

// Shared day/date-range logic for the kanban day grouping and the created-at
// filter. Everything resolves against the viewer's local calendar day: stored
// values are UTC, so `2026-11-12T23:00:00.000Z` is already the 13th in Lagos and
// comparing the raw strings would file it under the wrong day.

export const NO_DATE_KEY = 'no-date';

// When a card entered the status it is currently in. Stamped client-side on the
// row as `metadata.extra.enteredStatusAt`; rows that predate that (or were last
// changed elsewhere) fall back to when the row was created.
export const getCardGroupingDate = (row) => {
    const stamped = row?.metadata?.extra?.enteredStatusAt;
    const value = stamped || row?.created_at || null;
    if (!value) return null;
    const date = new Date(value);
    return isValid(date) ? date : null;
};

// Which day bucket a date belongs to, relative to `now`.
// `rank` is the day offset, so sections read oldest first and the newest cards
// sit at the bottom of the column — the same way new rows append today.
// Undated cards go above everything, out of the way of the live end.
export const getDayBucket = (date, now = new Date()) => {
    if (!date) return { key: NO_DATE_KEY, label: 'No date', rank: Number.MIN_SAFE_INTEGER };

    const diff = differenceInCalendarDays(startOfDay(date), startOfDay(now));
    const key = format(date, 'yyyy-MM-dd');

    if (diff === 0) return { key, label: 'Today', rank: 0 };
    if (diff === 1) return { key, label: 'Tomorrow', rank: 1 };
    if (diff > 1) return { key, label: format(date, 'EEE, d MMM'), rank: diff };
    if (diff === -1) return { key, label: 'Yesterday', rank: -1 };
    // Older than yesterday: one bucket per day. Dates within the last week read
    // better as weekday names.
    const label = diff > -7 ? format(date, 'EEEE') : format(date, 'd MMM yyyy');
    return { key, label, rank: diff };
};

// Group rows into ordered day buckets (oldest first), preserving each bucket's
// incoming order — the board passes rows already sorted by position, so a card
// created from an existing row's position keeps that spot within its day.
export const groupRowsByDay = (rows, resolveDate, now = new Date()) => {
    const buckets = new Map();

    (rows || []).forEach((row) => {
        const bucket = getDayBucket(resolveDate(row), now);
        if (!buckets.has(bucket.key)) {
            buckets.set(bucket.key, { ...bucket, rows: [] });
        }
        buckets.get(bucket.key).rows.push(row);
    });

    return Array.from(buckets.values()).sort((a, b) => a.rank - b.rank);
};

// ── Ranges, for the created-at filter ───────────────────────────────────────
// Each returns an inclusive [from, to] pair of local-day boundaries.

export const DATE_RANGE_PRESETS = [
    { id: 'today', label: 'Today' },
    { id: 'yesterday', label: 'Yesterday' },
    { id: 'last-week', label: 'Last week' },
    { id: 'last-month', label: 'Last month' },
];

export const getPresetRange = (presetId, now = new Date()) => {
    switch (presetId) {
        case 'today':
            return { from: startOfDay(now), to: endOfDay(now) };
        case 'yesterday': {
            const day = subDays(now, 1);
            return { from: startOfDay(day), to: endOfDay(day) };
        }
        case 'last-week': {
            // The whole of the previous calendar week, not a rolling 7 days.
            const day = subWeeks(now, 1);
            return { from: startOfWeek(day), to: endOfWeek(day) };
        }
        case 'last-month': {
            const day = subMonths(now, 1);
            return { from: startOfMonth(day), to: endOfMonth(day) };
        }
        default:
            return null;
    }
};

// Serialise a range for the URL: `YYYY-MM-DD..YYYY-MM-DD`, or a single day.
export const serializeRange = (range) => {
    if (!range?.from) return '';
    const from = format(range.from, 'yyyy-MM-dd');
    if (!range.to) return from;
    const to = format(range.to, 'yyyy-MM-dd');
    return from === to ? from : `${from}..${to}`;
};

export const parseRange = (value) => {
    if (!value || typeof value !== 'string') return null;
    const [fromPart, toPart] = value.split('..');
    const from = new Date(`${fromPart}T00:00:00`);
    if (!isValid(from)) return null;
    const to = toPart ? new Date(`${toPart}T00:00:00`) : from;
    if (!isValid(to)) return null;
    return { from: startOfDay(from), to: endOfDay(to) };
};

// Does a stored timestamp fall inside an inclusive local-day range?
export const isWithinRange = (value, range) => {
    if (!value || !range?.from) return false;
    const date = new Date(value);
    if (!isValid(date)) return false;
    const to = range.to ? range.to : endOfDay(range.from);
    return date >= range.from && date <= to;
};
